import { useLocalization } from "@fluent/react";
import { useCallback, useMemo, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useToastsContext } from "@lending/hooks/useToasts";

function useLendingCaptcha() {
  const { institution } = useGetInstitutionQuery();
  const { l10n } = useLocalization();
  const { showErrorToast } = useToastsContext();
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const captchaErrorText = l10n.getString(
    "personal-info-captcha-error",
    null,
    "Please verify you're not a robot."
  );

  const isCaptchaAdditionalFieldEnabled = useMemo(
    () =>
      institution.lending_ask_additional_fields?.some(
        (field) => field.name === "captcha" && field.value
      ) ?? false,
    [institution.lending_ask_additional_fields]
  );

  const shouldShowCaptcha = Boolean(
    institution.captcha_public_key && isCaptchaAdditionalFieldEnabled
  );

  const resetCaptcha = useCallback(() => {
    setCaptchaValue(null);
    recaptchaRef.current?.reset();
  }, []);

  const validateCaptcha = useCallback(() => {
    if (!shouldShowCaptcha || captchaValue) {
      return true;
    }

    showErrorToast(captchaErrorText);
    return false;
  }, [captchaErrorText, captchaValue, shouldShowCaptcha, showErrorToast]);

  const handleRecaptchaError = useCallback(
    (error: unknown) => {
      if (error !== "invalid recaptcha") {
        return false;
      }

      resetCaptcha();
      showErrorToast(captchaErrorText);
      return true;
    },
    [captchaErrorText, resetCaptcha, showErrorToast]
  );

  return {
    captchaValue,
    handleRecaptchaError,
    recaptchaRef,
    validateCaptcha,
    setCaptchaValue,
    shouldShowCaptcha,
    siteKey: institution.captcha_public_key,
  };
}

export { useLendingCaptcha };
